import type { ApolloError } from '@apollo/client';
import type { EmployeeImportFormat, ImportHistoryListQuery, ImportProgressFieldsFragment } from '@/common/graphql';

/** Raw row shape returned by the single, unified `listImportHistories` query. */
type ImportHistoryListItem = ImportHistoryListQuery['importHistoryList'][number];

/**
 * Common row shape for both Employee and Managerial Accounting import history rows. `__typename` is
 * GraphQL's own interface discriminant (not a new backend field) — it is what the Format column
 * checks to render the real format for Employee rows and the literal text "Managerial Accounting"
 * for Managerial Accounting rows. `format` is present only when `__typename` is `EmployeeImportHistory`.
 *
 * Field names mirror `ExportHistoryRowData` exactly (per the field-unification directive), not
 * the issue's literal `totalRowCount`/`completedRowCount`.
 */
export type ImportHistoryRowData = Pick<
  ImportHistoryListItem,
  | '__typename'
  | 'id'
  | 'status'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'totalRequestCount'
  | 'completedRequestCount'
  | 'errors'
> & {
  /** Only present for `__typename: 'EmployeeImportHistory'` rows. */
  format?: EmployeeImportFormat;
};

/** Normalizes one raw `listImportHistories` row (either concrete kind) to `ImportHistoryRowData`. */
export const toImportHistoryRow = (item: ImportHistoryListItem): ImportHistoryRowData => ({
  ...item,
  format: item.__typename === 'EmployeeImportHistory' ? item.format : undefined,
});

/**
 * Common projection shared by the unified Progress subscription's payload — the generated
 * `ImportProgressFields` fragment (declared on the shared `ExportImportProgress` interface),
 * narrowed to the fields the live-progress merge uses.
 */
export type ImportProgressItem = Pick<
  ImportProgressFieldsFragment,
  'id' | 'status' | 'createdAt' | 'updatedAt' | 'totalRequestCount' | 'completedRequestCount'
>;

/** Narrows a raw progress GraphQL item down to `ImportProgressItem`. */
export const toImportProgressItem = (item: ImportProgressFieldsFragment): ImportProgressItem => ({
  id: item.id,
  status: item.status,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  totalRequestCount: item.totalRequestCount,
  completedRequestCount: item.completedRequestCount,
});

/** Shape returned by the single `useImportHistory` hook. */
export interface UseImportHistoryResult {
  error: ApolloError | undefined;
  loading: boolean;
  refetch: () => void;
  rows: Array<ImportHistoryRowData>;
}
